/**
 * guided-options.js  v6.9
 * Dollar Blinds — Guided configurator with sticky progress rail
 *
 *  • Sticky vertical stepper rail to the right of the TEPO form
 *  • Guided flow: one accordion open at a time, auto-advances on selection
 *  • Rail dots: gold(sonar pulse)=current, gold=done, beige=waiting
 *  • Selected option's DESCRIPTION persists below the closed heading
 *  • Works on desktop, tablet, and mobile (rail compresses on narrow screens)
 *
 * v6.9 — Continue now also appears when you go BACK to a finished dropdown
 *        step to change your answer. Re-opening a step clears its previous
 *        confirmation, the button no longer requires the step to be the
 *        current one, and confirming a revisited step just closes it instead
 *        of doing nothing.
 * v6.8 — Dropdown steps never auto-advance. The shopper picks their option and
 *        then presses Continue; that press is the only thing that moves the
 *        step on. Changing the parent radio clears the confirmation so they
 *        are not silently skipped past a newly revealed dropdown.
 * v6.7 — A TEPO "preselect" no longer counts as the shopper's answer. The
 *        Motorization Accessories dropdown defaults to "No Remote or Charger",
 *        which made the step complete and close the instant the parent radio
 *        was chosen - the dropdown could never be opened. A dropdown is now
 *        satisfied only once clicked, and a Continue button appears so anyone
 *        happy with the default is not trapped.
 * v6.6 — Dropdown steps no longer stall. dropdownPicked() now reads the live
 *        DOM (label.selected / :checked / select value) instead of relying on
 *        a class-mutation event that never fires when Vue re-creates the list
 *        with .selected already set, and a delegated click handler advances
 *        the step directly. selectsOk() no longer requires the shade-division
 *        "Valid" message, which can never appear now that 2-in-1 is disabled
 *        and which was stalling every step containing a visible <select>.
 * v6.5 — Free-text ("Other") gating no longer uses a typing-pause timer
 *        (that closed the step mid-thought). It now advances ONLY on an
 *        explicit signal: pressing Enter, leaving the field (blur), or
 *        tapping the injected "Continue" button — and only once non-empty.
 * v6.4 — "Other"-style free-text gating introduced (requiredTextOk).
 * v6.3 — Chevron is a CSS pseudo-element; injected summary/desc moved below
 *        the heading so the heading (title, help icon, chevron) never shifts.
 * v5.9 — Selected option's description persists when the step closes.
 * v5.8 — Heading state badges hidden from view (kept as rail source-of-truth).
 * v5.7 — Current-step dot emits expanding "sonar" pulse rings.
 * v5.5 — Mobile: form forced full-width; rail overlays right edge & floats.
 *
 * TEPO DOM facts:
 *  • Each step: .tcustomizer-field.tcustomizer-group-option
 *  • Open/close: .tcustomizer-accordion__content.opened
 *  • Description block: .tcustomizer-field--Description
 *  • The toggle chevron is a CSS pseudo-element on the heading (no element).
 *  • Room Placement + Add side channels both use radio name field 602689
 *    → isolateRadioNames() breaks the cross-wrapper radio group at init
 *
 * Deploy:
 *   Upload as guided-options.js → in main-product.liquid after TEPO script:
 *   {{ 'guided-options.js' | asset_url | script_tag }}
 */

(function () {
  'use strict';

  var INIT_DELAY    = 950;
  var ADVANCE_DELAY = 400;
  var OBS_DEBOUNCE  = 280;
  var SCROLL_OFFSET = 100;

  /* Show the selected option's description below the CLOSED heading */
  var SHOW_DESC_ON_CLOSED = false;
  /* Where to find the description text inside an accordion's content.
     Confirmed against live DOM: TEPO uses .tcustomizer-field--Description. */
  var DESC_SELECTORS = [
    '.tcustomizer-field--Description',
    '.tcustomizer-description',
    '.tcustomizer-field--RichText',
    '.tcustomizer-richtext',
    '.tcustomizer-field--Paragraph'
  ];

  /* ═══════════════════════════════════════════════════
     STYLES
  ═══════════════════════════════════════════════════ */
  function injectStyles() {
    if (document.getElementById('db-go-styles')) return;
    var s = document.createElement('style');
    s.id = 'db-go-styles';
    s.textContent = `
      /* ── Sticky stepper rail ── */
      #db-progress-widget {
        position: fixed;
        z-index: 9999;
        pointer-events: none;
        transition: top .15s ease-out;
      }
      #db-progress-rail {
        pointer-events: auto;
        background: #fff;
        border: 1px solid #f0ece0;
        border-radius: 22px;
        padding: 22px 16px;
        box-shadow: 0 6px 22px rgba(0,0,0,.10);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0;
        position: relative;
      }

      .db-step-wrap {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
      }
      .db-step-wrap:not(:last-child)::after {
        content: '';
        width: 3px;
        height: 38px;
        background: #e5e5e5;
        transition: background .35s ease;
        border-radius: 2px;
      }
      .db-step-wrap.db-st-done:not(:last-child)::after {
        background: #a0801e;
      }

      .db-step-dot {
        width: 30px;
        height: 30px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        font-weight: 700;
        font-family: 'Glacial Indifference', sans-serif;
        flex-shrink: 0;
        border: 2px solid #d6d6d6;
        color: #999;
        background: #fff;
        cursor: pointer;
        transition: all .3s cubic-bezier(.4, 0, .2, 1);
        position: relative;
        z-index: 1;
      }
      .db-step-dot:hover { transform: scale(1.08); }

      /* DONE — gold */
      .db-step-wrap.db-st-done .db-step-dot {
        background: #a0801e;
        border-color: #a0801e;
        color: #fff;
      }
      .db-step-wrap.db-st-done .db-step-dot::after {
        content: '✓';
        position: absolute;
        font-size: 14px;
      }
      .db-step-wrap.db-st-done .db-step-num { display: none; }

      /* ACTIVE / CURRENT — gold, with sonar pulse rings (see below) */
      .db-step-wrap.db-st-active .db-step-dot {
        background: #a0801e;
        border-color: #a0801e;
        color: #fff;
        box-shadow: 0 0 0 3px rgba(160,128,30,.18);
      }
      .db-step-wrap.db-st-active .db-step-num { color: #fff; }

      /* WAITING — soft beige, locked */
      .db-step-wrap:not(.db-st-done):not(.db-st-active) .db-step-dot {
        cursor: not-allowed;
        opacity: 0.85;
        background: #f4f0e6;
        border-color: #ddd3b5;
        color: #b0a06a;
      }
      .db-step-wrap:not(.db-st-done):not(.db-st-active) .db-step-dot:hover {
        transform: none;
      }

      /* ── Sonar "pulse" rings — expand outward from the active dot ── */
      .db-step-ring,
      .db-step-ring::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 30px;
        height: 30px;
        margin: -15px 0 0 -15px;
        border-radius: 50%;
        background: rgba(160,128,30,.35);
        opacity: 0;
        pointer-events: none;
        z-index: 0;
      }
      .db-step-wrap.db-st-active .db-step-ring {
        animation: db-sonar 1.8s ease-out infinite;
      }
      .db-step-wrap.db-st-active .db-step-ring::before {
        animation: db-sonar 1.8s ease-out infinite;
        animation-delay: .9s;
      }
      @keyframes db-sonar {
        0%   { transform: scale(1);   opacity: .6; }
        100% { transform: scale(2.6); opacity: 0;  }
      }

      @keyframes db-shake {
        0%, 100% { transform: translateX(0); }
        20%, 60% { transform: translateX(-4px); }
        40%, 80% { transform: translateX(4px); }
      }
      @keyframes db-pulse {
        0%   { box-shadow: 0 0 0 4px rgba(160,128,30,.2); }
        50%  { box-shadow: 0 0 0 10px rgba(160,128,30,.45); }
        100% { box-shadow: 0 0 0 4px rgba(160,128,30,.2); }
      }

      .db-step-dot::before {
        content: attr(data-label);
        position: absolute;
        right: 42px;
        top: 50%;
        transform: translateY(-50%) translateX(6px);
        background: #2c2c2c;
        color: #fff;
        font-size: 11px;
        font-weight: 500;
        font-family: 'Glacial Indifference', sans-serif;
        white-space: nowrap;
        padding: 5px 10px;
        border-radius: 4px;
        pointer-events: none;
        opacity: 0;
        transition: opacity .15s, transform .15s;
        z-index: 10;
      }
      .db-step-dot::after {
        content: '';
        position: absolute;
        right: 36px;
        top: 50%;
        transform: translateY(-50%);
        border: 5px solid transparent;
        border-left-color: #2c2c2c;
        opacity: 0;
        transition: opacity .15s;
        pointer-events: none;
        z-index: 10;
      }
      .db-step-dot:hover::before { opacity: 1; transform: translateY(-50%) translateX(0); }
      .db-step-dot:hover::after  { opacity: 1; }
      .db-step-wrap.db-st-done .db-step-dot::after {
        right: auto;
        border: none;
        opacity: 1;
      }

      /* ── Accordion heading badges ── (HIDDEN, kept as rail source-of-truth) */
      .db-acc-badge {
        display: none !important;
      }

      .tcustomizer-accordion__heading:has(.db-badge-waiting) {
        cursor: not-allowed;
      }

      /* ── Injected ✓ summary — sits BELOW the heading, not inside it ── */
      .db-head-summary {
        display: none;
        font-size: 14px;
        color: #a0801e;
        font-family: 'Glacial Indifference', sans-serif;
        font-weight: 500;
        margin: 0 0 6px;
        padding: 0 12px;
      }

      /* ── Persistent description — sits BELOW the heading when closed ── */
      .db-head-desc {
        display: none;
        margin: 6px 12px 10px;
        padding: 10px 12px;
        background: #faf8f2;
        border: 1px solid #ece4cf;
        border-left: 3px solid #a0801e;
        border-radius: 0 8px 8px 0;
        font-size: 16px;
        line-height: 1.5;
        color: #5a5a5a;
        font-family: 'Glacial Indifference', sans-serif;
        font-weight: 400;
      }

      .db-hint {
        display: none;
        margin: 8px 0 4px;
        padding: 8px 12px;
        background: #fef9ec;
        border: 1px solid #f0c040;
        border-radius: 6px;
        font-size: 13px;
        color: #7a5c00;
        font-family: 'Glacial Indifference', sans-serif;
      }
      .db-hint.db-on { display: block; }

      /* ── "Continue" button for free-text ("Other") steps ──
         Lets users on any device confirm they're done typing without relying
         on Enter or tapping away. Shown only when a free-text gate is active. */
      .db-continue-btn {
        display: none;
        margin: 4px 0 8px;
        padding: 10px 22px;
        background: #a0801e;
        color: #fff;
        border: none;
        border-radius: 8px;
        font-size: 15px;
        font-weight: 500;
        font-family: 'Glacial Indifference', sans-serif;
        cursor: pointer;
        transition: opacity .2s, background .2s;
      }
      .db-continue-btn:hover { background: #8a6d18; }
      .db-continue-btn.db-on { display: inline-block; }
      .db-continue-btn:disabled { opacity: .45; cursor: not-allowed; }

      /* ─── Tablet (≤1024px) — slightly smaller rail ─── */
      @media (max-width: 1024px) {
        #db-progress-rail {
          padding: 16px 11px;
          border-radius: 18px;
        }
        .db-step-dot { width: 26px; height: 26px; font-size: 11px; }
        .db-step-wrap:not(:last-child)::after { height: 28px; }
        .db-step-ring,
        .db-step-ring::before { width: 26px; height: 26px; margin: -13px 0 0 -13px; }
      }

      /* ─── Mobile (≤749px) — compact rail, pinned to right edge ─── */
      @media (max-width: 749px) {
        #db-progress-rail {
          padding: 10px 7px;
          border-radius: 16px;
          box-shadow: 0 4px 14px rgba(0,0,0,.12);
        }
        .db-step-dot {
          width: 22px;
          height: 22px;
          font-size: 10px;
          border-width: 1.5px;
        }
        .db-step-wrap:not(:last-child)::after {
          height: 18px;
          width: 2px;
        }
        .db-step-wrap.db-st-done .db-step-dot::after { font-size: 11px; }
        .db-step-dot::before { display: none; }

        .db-step-ring,
        .db-step-ring::before { width: 22px; height: 22px; margin: -11px 0 0 -11px; }

        .tcustomizer-customization-form { margin-right: 0 !important; }

        .tcustomizer-accordion__heading { padding-right: 30px !important; }
        .db-head-summary { padding-right: 34px; }
        .db-head-desc { margin-right: 34px; }
        .db-continue-btn { width: 100%; padding: 12px; }
      }

      @media (prefers-reduced-motion: reduce) {
        .db-step-wrap.db-st-active .db-step-ring,
        .db-step-wrap.db-st-active .db-step-ring::before { animation: none; }
      }
    `;
    document.head.appendChild(s);
  }

  /* ═══════════════════════════════════════════════════
     DOM HELPERS
  ═══════════════════════════════════════════════════ */

  function getWrappers() {
    return Array.from(
      document.querySelectorAll('.tcustomizer-field.tcustomizer-group-option')
    ).filter(function (el) {
      return !!el.querySelector('.tcustomizer-accordion__heading') &&
             el.offsetParent !== null;
    });
  }

  function accEl(w)  { return w.querySelector('.tcustomizer-accordion'); }
  function cntEl(w)  { return w.querySelector('.tcustomizer-accordion__content'); }
  function hdEl(w)   { return w.querySelector('.tcustomizer-accordion__heading'); }
  function isOpen(w) { var c = cntEl(w); return c && c.classList.contains('opened'); }

  function openAcc(w)  { var c = cntEl(w); if (c) c.classList.add('opened'); }
  function closeAcc(w) { var c = cntEl(w); if (c) c.classList.remove('opened'); }

  function scrollTo(w) {
    var top = w.getBoundingClientRect().top + window.pageYOffset - SCROLL_OFFSET;
    window.scrollTo({ top: top, behavior: 'smooth' });
  }

  function isVisible(el) {
    if (!el || el.offsetParent === null) return false;
    var n = el;
    while (n && n !== document.body) {
      if (window.getComputedStyle(n).display === 'none') return false;
      n = n.parentElement;
    }
    return true;
  }

  /* ═══════════════════════════════════════════════════
     SELECTION READING
  ═══════════════════════════════════════════════════ */

  function getPrimary(w) {
    var r = w.querySelector('input[type="radio"]:checked');
    return r ? r.value : null;
  }

  function hasVisibleDropdown(w) {
    var fields = w.querySelectorAll('.tcustomizer-field--Dropdown');
    for (var i = 0; i < fields.length; i++) {
      if (isVisible(fields[i])) return true;
    }
    return false;
  }

  function dropdownFields(w) {
    var out = [];
    var fields = w.querySelectorAll('.tcustomizer-field--Dropdown');
    for (var i = 0; i < fields.length; i++) if (isVisible(fields[i])) out.push(fields[i]);
    return out;
  }

  /* True when this step contains a visible dropdown at all. */
  function hasDropdownGate(w) {
    return dropdownFields(w).length > 0;
  }

  /* Set only by the Continue button. */
  function dropdownConfirmed(w) {
    return w.getAttribute('data-db-confirmed') === '1';
  }

  /* First visible dropdown the shopper has not opened yet, or null. Used only
     to decide whether to keep nudging them with the hint. */
  function pendingDropdown(w) {
    var fields = dropdownFields(w);
    for (var i = 0; i < fields.length; i++) {
      if (fields[i].getAttribute('data-db-picked') !== '1') return fields[i];
    }
    return null;
  }

  /* A step holding a dropdown NEVER auto-advances.

     TEPO preselects a value (Motorization Accessories defaults to "No Remote
     or Charger"), so there is no trustworthy "they answered it" signal, and
     advancing on the first click closed the step while the list was still
     open. The shopper picks their option and then presses Continue - that
     press is the only thing that moves the step on. */
  function dropdownPicked(w) {
    return !hasDropdownGate(w) || dropdownConfirmed(w);
  }

  function shadeWidthValid(w) {
    var els = w.querySelectorAll('button, div, span, p');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (!isVisible(el)) continue;
      if (el.children.length > 2) continue;
      var text = (el.textContent || '').trim().toLowerCase();
      if (text.indexOf('valid') !== -1 &&
          text.indexOf('invalid') === -1 &&
          text.indexOf('must') === -1 &&
          text.indexOf('error') === -1 &&
          text.indexOf('currently') === -1) {
        return true;
      }
    }
    return false;
  }

  function selectsOk(w) {
    var sels = w.querySelectorAll('select');
    var visible = [];
    for (var i = 0; i < sels.length; i++) if (isVisible(sels[i])) visible.push(sels[i]);
    if (!visible.length) return true;
    /* This used to ALWAYS defer to shadeWidthValid() - the shade-division
       panel's "Valid" message. With 2-in-1 disabled that message can never
       appear, so every step holding a visible <select> stalled forever.
       Keep the old gate for the shade-division panel itself (so restoring
       2-in-1 still enforces left + right = total width), and fall back to
       "every visible select has a value" everywhere else. */
    if (window.is2in1Selected === true) return shadeWidthValid(w);
    for (var j = 0; j < visible.length; j++) {
      var v = visible[j].value;
      if (v === '' || v === null || v === 'null') return false;
    }
    return true;
  }

  /* Return the visible, required free-text input in this step (the "Other"
     notes box), or null if none is showing. */
  function getRequiredTextEl(w) {
    var inputs = w.querySelectorAll('input[type="text"], input:not([type]), textarea');
    for (var i = 0; i < inputs.length; i++) {
      var el = inputs[i];
      if (el.classList && el.classList.contains('tcustomizer-quantity-input')) continue;
      if (!isVisible(el)) continue;
      var required = el.required ||
                     el.getAttribute('aria-required') === 'true' ||
                     (el.closest('.tcustomizer-field') &&
                      el.closest('.tcustomizer-field').querySelector('.tcustomizer-asterisk'));
      if (required) return el;
    }
    return null;
  }

  /* True unless a visible, required free-text input exists and is empty. */
  function requiredTextOk(w) {
    var el = getRequiredTextEl(w);
    if (!el) return true;                 // no free-text gate active
    return (el.value || '').trim() !== ''; // must be non-empty
  }

  function isComplete(w) {
    if (!getPrimary(w)) return false;
    if (!selectsOk(w)) return false;
    if (!requiredTextOk(w)) return false;
    if (hasVisibleDropdown(w) && !dropdownPicked(w)) return false;
    return true;
  }

  function getLabel(w) {
    var h = hdEl(w);
    if (!h) return '';
    var span = h.querySelector('span');
    return span ? span.textContent.trim() : h.textContent.trim().split('\n')[0].trim();
  }

  /* ═══════════════════════════════════════════════════
     CONTINUE BUTTON (free-text steps)
  ═══════════════════════════════════════════════════ */

  /* Ensure a "Continue" button exists at the bottom of a step's content.
     Shown while the step is waiting on a required free-text input ("Other"),
     or on any dropdown step that has not been confirmed yet. */
  function syncContinueBtn(w) {
    var cnt = cntEl(w);
    if (!cnt) return;
    /* Open is enough - a revisited earlier step still needs its Continue. */
    var active = isOpen(w);
    var textEl = active ? getRequiredTextEl(w) : null;
    var needsConfirm = active && hasDropdownGate(w) && !dropdownConfirmed(w);
    var btn = cnt.querySelector(':scope > .db-continue-btn');

    if (!textEl && !needsConfirm) {     // nothing to gate on -> hide any button
      if (btn) btn.classList.remove('db-on');
      return;
    }
    if (!btn) {
      btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'db-continue-btn';
      btn.textContent = 'Continue';
      btn.addEventListener('click', function () {
        /* Confirm the step: accept whatever the dropdowns currently show. */
        w.setAttribute('data-db-confirmed', '1');
        dropdownFields(w).forEach(function (f) { f.setAttribute('data-db-picked', '1'); });
        if (getRequiredTextEl(w)) onTextInput(w, true);
        else onDropdownPick(w);
      });
      cnt.appendChild(btn);
    }
    btn.classList.add('db-on');
    btn.disabled = textEl ? !requiredTextOk(w) : false;
  }

  /* ═══════════════════════════════════════════════════
     DESCRIPTION CAPTURE
  ═══════════════════════════════════════════════════ */

  function getDescEl(w) {
    var c = cntEl(w);
    if (!c) return null;

    for (var i = 0; i < DESC_SELECTORS.length; i++) {
      var hit = c.querySelector(DESC_SELECTORS[i]);
      if (hit) return hit;
    }

    var best = null, bestLen = 0;
    var cand = c.querySelectorAll('p, div, span');
    for (var j = 0; j < cand.length; j++) {
      var e = cand[j];
      if (e.querySelector('input, select, button, img, label')) continue;
      if (e.children.length > 3) continue;
      var t = (e.textContent || '').trim();
      if (t.length >= 40 && t.length > bestLen) { best = e; bestLen = t.length; }
    }
    return best;
  }

  function getDescText(w) {
    var el = getDescEl(w);
    if (!el) return '';
    return (el.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function setHeadDesc(w, text) {
    if (!SHOW_DESC_ON_CLOSED) return;
    var acc = accEl(w); if (!acc) return;
    var cnt = cntEl(w);
    var d = acc.querySelector(':scope > .db-head-desc');
    if (!text) { if (d) d.style.display = 'none'; return; }
    if (!d) {
      d = document.createElement('div');
      d.className = 'db-head-desc';
      if (cnt) acc.insertBefore(d, cnt); else acc.appendChild(d);
    }
    d.textContent = text;
    d.style.display = 'block';
  }

  function refreshHeadDescs() {
    if (!SHOW_DESC_ON_CLOSED) return;
    wrappers.forEach(function (w) {
      var cachedDesc = w.getAttribute('data-db-desc');
      if (cachedDesc && !isOpen(w)) setHeadDesc(w, cachedDesc);
      else setHeadDesc(w, '');
    });
  }

  /* ═══════════════════════════════════════════════════
     HINT
  ═══════════════════════════════════════════════════ */

  function getHint(w) {
    var a = accEl(w); if (!a) return null;
    var h = a.querySelector('.db-hint'); if (h) return h;
    h = document.createElement('div');
    h.className = 'db-hint';
    h.textContent = '↓ Please complete the options below to continue';
    var hd = hdEl(w);
    if (hd && hd.nextSibling) a.insertBefore(h, hd.nextSibling);
    else a.appendChild(h);
    return h;
  }
  function showHint(w) { var h = getHint(w); if (h) h.classList.add('db-on'); }
  function hideHint(w) { var a = accEl(w); if (!a) return; var h = a.querySelector('.db-hint'); if (h) h.classList.remove('db-on'); }

  /* ═══════════════════════════════════════════════════
     BADGE + HEADING SUMMARY
  ═══════════════════════════════════════════════════ */

  function setBadge(w, state) {
    var h = hdEl(w); if (!h) return;
    var b = h.querySelector('.db-acc-badge');
    if (!b) { b = document.createElement('span'); b.className = 'db-acc-badge'; h.appendChild(b); }
    b.className = 'db-acc-badge';
    if (state === 'done')           { b.classList.add('db-badge-done');      b.textContent = 'Done'; }
    else if (state === 'selecting') { b.classList.add('db-badge-selecting'); b.textContent = 'Selecting'; }
    else                            { b.classList.add('db-badge-waiting');   b.textContent = 'Waiting'; }
  }

  function setHeadSummary(w, text) {
    var acc = accEl(w); if (!acc) return;
    var cnt = cntEl(w);
    var s = acc.querySelector(':scope > .db-head-summary');
    if (!text) { if (s) s.style.display = 'none'; return; }
    if (!s) {
      s = document.createElement('div');
      s.className = 'db-head-summary';
      var before = acc.querySelector(':scope > .db-head-desc') || cnt;
      if (before) acc.insertBefore(s, before); else acc.appendChild(s);
    }
    s.textContent = '✓ ' + text;
    s.style.display = 'block';
  }

  function markDone(w) {
    var val = getPrimary(w) || w.getAttribute('data-db-summary') || '';
    if (val) w.setAttribute('data-db-summary', val);
    var desc = getDescText(w);
    if (desc) w.setAttribute('data-db-desc', desc);
    setBadge(w, 'done');
    setHeadSummary(w, val.length > 28 ? val.slice(0, 26) + '…' : val);
    hideHint(w);
  }

  /* ═══════════════════════════════════════════════════
     SIDEBAR
  ═══════════════════════════════════════════════════ */

  var sideStepEls = [];

  function buildLayout(wrappers) {
    var existing = document.getElementById('db-progress-widget');
    if (existing) existing.remove();

    var widget = document.createElement('div');
    widget.id = 'db-progress-widget';

    var rail = document.createElement('div');
    rail.id = 'db-progress-rail';

    sideStepEls = [];
    wrappers.forEach(function (w, i) {
      var stepWrap = document.createElement('div');
      stepWrap.className = 'db-step-wrap';

      var dot = document.createElement('div');
      dot.className = 'db-step-dot';
      dot.setAttribute('data-label', getLabel(w));
      var num = document.createElement('span');
      num.className = 'db-step-num';
      num.textContent = i + 1;
      dot.appendChild(num);

      var ring = document.createElement('span');
      ring.className = 'db-step-ring';
      dot.appendChild(ring);

      dot.addEventListener('click', function () {
        var targetIdx = wrappers.indexOf(w);
        if (targetIdx > currentStep) {
          dot.style.animation = 'none';
          requestAnimationFrame(function () { dot.style.animation = 'db-shake .35s ease'; });
          return;
        }
        scrollTo(w);
        if (!isOpen(w)) {
          wrappers.forEach(function (ow, oi) { if (oi !== targetIdx) closeAcc(ow); });
          openAcc(w);
        }
        currentStep = targetIdx;
        setBadge(w, 'selecting');
        refreshHeadDescs();
        syncContinueBtn(w);
        updateSidebar(wrappers, currentStep);
      });

      stepWrap.appendChild(dot);
      rail.appendChild(stepWrap);
      sideStepEls.push({ row: stepWrap, dot: dot });
    });

    widget.appendChild(rail);
    document.body.appendChild(widget);

    positionRail();
    if (!window.__dbRailResize) {
      window.__dbRailResize = true;
      window.addEventListener('resize', positionRail);
      window.addEventListener('scroll', positionRail, { passive: true });
    }
  }

  function positionRail() {
    var widget = document.getElementById('db-progress-widget');
    var form   = document.querySelector('.tcustomizer-customization-form');
    if (!widget || !form) return;

    var rail     = widget.querySelector('#db-progress-rail');
    var railH    = rail ? rail.offsetHeight : 300;
    var railW    = rail ? rail.offsetWidth  : 60;
    var vpH      = window.innerHeight;
    var vpW      = window.innerWidth;
    var gap      = 12;
    var edgeGap  = 8;

    if (vpW <= 749) {
      form.style.marginRight = '';
      var formRectM = form.getBoundingClientRect();
      widget.style.left  = 'auto';
      widget.style.right = edgeGap + 'px';
      var idealTopM = (vpH - railH) / 2;
      var minTopM   = formRectM.top;
      var maxTopM   = formRectM.bottom - railH;
      if (maxTopM < minTopM) {
        widget.style.top = minTopM + 'px';
      } else {
        widget.style.top = Math.max(minTopM, Math.min(idealTopM, maxTopM)) + 'px';
      }
      return;
    }

    form.style.marginRight = '';
    var formRect = form.getBoundingClientRect();
    var spaceRight = vpW - formRect.right;
    var neededSpace = railW + gap + edgeGap;

    var leftPx;
    if (spaceRight >= neededSpace) {
      leftPx = formRect.right + gap;
    } else {
      var pushBy = neededSpace - spaceRight;
      form.style.marginRight = pushBy + 'px';
      formRect = form.getBoundingClientRect();
      leftPx = formRect.right + gap;
    }

    widget.style.left  = leftPx + 'px';
    widget.style.right = 'auto';

    var idealTop = (vpH - railH) / 2;
    var minTop   = formRect.top;
    var maxTop   = formRect.bottom - railH;

    if (maxTop < minTop) {
      widget.style.top = minTop + 'px';
      return;
    }
    var top = Math.max(minTop, Math.min(idealTop, maxTop));
    widget.style.top = top + 'px';
  }

  function updateSidebar(wrappers, currentStep) {
    sideStepEls.forEach(function (el, i) {
      var stepWrap = el.row;
      var dot = el.dot;
      var w = wrappers[i];
      stepWrap.classList.remove('db-st-done', 'db-st-active');
      var base = w ? getLabel(w) : '';

      var a = w ? accEl(w) : null;
      var isDone = !!(a && a.querySelector('.db-acc-badge.db-badge-done'));

      if (isDone) {
        stepWrap.classList.add('db-st-done');
        var cached = w.getAttribute('data-db-summary');
        dot.setAttribute('data-label', cached ? base + ': ' + cached : base);
      } else if (i === currentStep) {
        stepWrap.classList.add('db-st-active');
        dot.setAttribute('data-label', base);
      } else {
        dot.setAttribute('data-label', base);
      }
    });
  }

  /* ═══════════════════════════════════════════════════
     STATE + CORE LOGIC
  ═══════════════════════════════════════════════════ */

  var wrappers    = [];
  var currentStep = 0;
  var advTimer    = null;

  function applyStates() {
    wrappers.forEach(function (w, i) {
      if (i < currentStep) {
        closeAcc(w);
        var cached = w.getAttribute('data-db-summary');
        if (cached) {
          setBadge(w, 'done');
          setHeadSummary(w, cached.length > 28 ? cached.slice(0, 26) + '…' : cached);
          var dcache = w.getAttribute('data-db-desc');
          if (!dcache) {
            var dnow = getDescText(w);
            if (dnow) { w.setAttribute('data-db-desc', dnow); dcache = dnow; }
          }
          setHeadDesc(w, dcache || '');
        } else {
          setBadge(w, 'waiting');
          setHeadSummary(w, '');
          setHeadDesc(w, '');
        }
        syncContinueBtn(w);
      } else if (i === currentStep) {
        openAcc(w);
        var existingDone = accEl(w) && accEl(w).querySelector('.db-acc-badge.db-badge-done');
        if (!existingDone) setBadge(w, 'selecting');
        setHeadDesc(w, '');
        syncContinueBtn(w);
      } else {
        closeAcc(w);
        setBadge(w, 'waiting');
        setHeadDesc(w, '');
        syncContinueBtn(w);
      }
    });
    updateSidebar(wrappers, currentStep);
  }

  function advanceTo(next) {
    var fresh = getWrappers();
    if (fresh.length !== wrappers.length) {
      wrappers = fresh;
      buildLayout(wrappers);
    }

    if (next >= wrappers.length) {
      currentStep = wrappers.length;
      wrappers.forEach(function (w) { closeAcc(w); });
      applyStates();
      updateSidebar(wrappers, currentStep);
      return;
    }

    if (wrappers[currentStep]) closeAcc(wrappers[currentStep]);
    currentStep = next;
    applyStates();
    setTimeout(function () { scrollTo(wrappers[currentStep]); }, 150);
  }

  function handleChange(w) {
    if (!w) return;
    var idx = wrappers.indexOf(w);
    if (idx === -1) return;

    if (idx < currentStep) {
      var v = getPrimary(w);
      if (v) {
        w.setAttribute('data-db-summary', v);
        markDone(w);
        setHeadDesc(w, w.getAttribute('data-db-desc') || '');
        updateSidebar(wrappers, currentStep);
      }
      if (isOpen(w) && hasDropdownGate(w)) {
        w.removeAttribute('data-db-confirmed');
        dropdownFields(w).forEach(function (f) { f.removeAttribute('data-db-picked'); });
        syncContinueBtn(w);
      }
      return;
    }
    if (idx !== currentStep) return;


    /* Changing the radio can reveal a different dropdown (or re-reveal this
       one), so a previous Continue no longer applies - make them confirm again
       rather than silently skipping the step. */
    if (hasDropdownGate(w)) {
      w.removeAttribute('data-db-confirmed');
      dropdownFields(w).forEach(function (f) { f.removeAttribute('data-db-picked'); });
    }
    var primary = getPrimary(w);
    if (!primary) { hideHint(w); clearTimeout(advTimer); return; }

    clearTimeout(advTimer);
    advTimer = setTimeout(function () {
      if (idx !== currentStep || !getPrimary(w)) return;
      if (hasVisibleDropdown(w) && !dropdownPicked(w)) { showHint(w); syncContinueBtn(w); return; }
      if (!selectsOk(w)) { showHint(w); return; }
      if (!requiredTextOk(w)) {            // "Other" chosen but notes empty
        showHint(w);
        syncContinueBtn(w);                // reveal the Continue button (disabled)
        return;
      }
      hideHint(w);
      markDone(w);
      advTimer = setTimeout(function () {
        if (idx !== currentStep) return;
        advanceTo(currentStep + 1);
      }, ADVANCE_DELAY);
    }, 80);
  }

  function onDropdownPick(w) {
    var idx = wrappers.indexOf(w);
    if (idx === -1) return;
    if (!getPrimary(w)) return;
    if (!selectsOk(w)) { showHint(w); return; }
    if (!requiredTextOk(w)) { showHint(w); syncContinueBtn(w); return; }
    hideHint(w);
    markDone(w);

    /* Revisiting an earlier, already-completed step: accept the change and
       close it again, rather than dragging them back through the whole flow. */
    if (idx !== currentStep) {
      closeAcc(w);
      refreshHeadDescs();
      syncContinueBtn(w);
      updateSidebar(wrappers, currentStep);
      return;
    }

    clearTimeout(advTimer);
    advTimer = setTimeout(function () {
      if (idx !== currentStep) return;
      advanceTo(currentStep + 1);
    }, ADVANCE_DELAY);
  }

  function onSelectChange(w) {
    var idx = wrappers.indexOf(w);
    if (idx === -1 || idx !== currentStep) return;
    if (!getPrimary(w)) return;

    clearTimeout(advTimer);
    advTimer = setTimeout(function () {
      if (idx !== currentStep || !getPrimary(w)) return;
      if (!selectsOk(w)) { showHint(w); return; }
      if (!requiredTextOk(w)) { showHint(w); syncContinueBtn(w); return; }
      if (hasVisibleDropdown(w) && !dropdownPicked(w)) { showHint(w); syncContinueBtn(w); return; }
      hideHint(w);
      markDone(w);
      advTimer = setTimeout(function () {
        if (idx !== currentStep) return;
        advanceTo(currentStep + 1);
      }, ADVANCE_DELAY);
    }, 80);
  }

  /* Free-text confirm — for the "Other" notes box. Called ONLY on explicit
     intent (Enter, blur, or the Continue button). Never on a typing pause. */
  function onTextInput(w, viaEnter) {
    var idx = wrappers.indexOf(w);
    if (idx === -1 || idx !== currentStep) return;
    if (!getPrimary(w)) return;
    if (!selectsOk(w)) { showHint(w); return; }
    if (!requiredTextOk(w)) { showHint(w); syncContinueBtn(w); return; }  // still empty
    if (hasVisibleDropdown(w) && !dropdownPicked(w)) { showHint(w); syncContinueBtn(w); return; }
    hideHint(w);
    markDone(w);
    clearTimeout(advTimer);
    advTimer = setTimeout(function () {
      if (idx !== currentStep) return;
      advanceTo(currentStep + 1);
    }, viaEnter ? 120 : ADVANCE_DELAY);
  }

  function debounce(fn, ms) {
    var t;
    return function () { var a = arguments, c = this; clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, ms); };
  }

  function isolateRadioNames() {
    wrappers.forEach(function (w, idx) {
      var radios = w.querySelectorAll('input[type="radio"]');
      radios.forEach(function (r) {
        if (!r.name || r.getAttribute('data-db-original-name')) return;
        var orig = r.name;
        r.setAttribute('data-db-original-name', orig);
        r.name = orig + '__db' + idx;
      });
    });
  }

  /* ═══════════════════════════════════════════════════
     ATTACH LISTENERS
  ═══════════════════════════════════════════════════ */
  function attachListeners() {
    var form = document.querySelector('.tcustomizer-customization-form');
    if (!form) return;
    var widget = document.querySelector('.tcustomizer-widget') || form;

    form.addEventListener('change', function (e) {
      if (e.target.type !== 'radio') return;
      handleChange(e.target.closest('.tcustomizer-field.tcustomizer-group-option'));
    });

    function isTextField(el) {
      if (!el) return false;
      if (el.classList && el.classList.contains('tcustomizer-quantity-input')) return false;
      return el.tagName === 'TEXTAREA' ||
             (el.tagName === 'INPUT' && (el.type === 'text' || !el.getAttribute('type')));
    }

    // Free-text gating for "Other" (and any required text/textarea).
    // NO typing-pause timer — that would close the step mid-thought.
    // While typing we only keep the hint + Continue button in sync; we
    // advance ONLY on Enter, blur (leaving the field), or the Continue button.
    form.addEventListener('input', function (e) {
      var el = e.target;
      if (!isTextField(el)) return;
      var w = el.closest('.tcustomizer-field.tcustomizer-group-option');
      if (!w) return;
      if (!requiredTextOk(w)) showHint(w); else hideHint(w);
      syncContinueBtn(w);          // enable/disable Continue as they type
    });
    form.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      var el = e.target;
      if (!isTextField(el)) return;
      var w = el.closest('.tcustomizer-field.tcustomizer-group-option');
      if (!w) return;
      e.preventDefault();          // don't submit the form on Enter
      onTextInput(w, true);
    });
    form.addEventListener('blur', function (e) {
      var el = e.target;
      if (!isTextField(el)) return;
      var w = el.closest('.tcustomizer-field.tcustomizer-group-option');
      if (!w) return;
      onTextInput(w, true);        // advance when they leave the field (if filled)
    }, true);                      // capture: blur doesn't bubble

    var dropObs = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.attributeName !== 'class') continue;
        var t = m.target;
        if (!t.classList || !t.classList.contains('tcustomizer-dropdown-image__label')) continue;
        if (!t.classList.contains('selected')) continue;
        var w = t.closest('.tcustomizer-field.tcustomizer-group-option');
        if (!w) continue;
        var f = t.closest('.tcustomizer-field--Dropdown');
        if (f) f.setAttribute('data-db-picked', '1');
        syncContinueBtn(w);   // no auto-advance: Continue does that
        return;
      }
    });
    dropObs.observe(form, { attributes: true, subtree: true, attributeFilter: ['class'] });

    /* Vue re-creates the option list with .selected already set, so the
       class-mutation observer above can miss a pick entirely. Catch the click
       directly as well. */
    /* Mark the SPECIFIC dropdown the shopper interacted with. Vue re-creates
       the list with .selected already set, so the class observer above can miss
       a pick entirely - this click path is the reliable one. */
    form.addEventListener('click', function (e) {
      var lbl = e.target.closest && e.target.closest('.tcustomizer-dropdown-image__label, .tcustomizer__dropdown-item');
      if (!lbl) return;
      var w = lbl.closest('.tcustomizer-field.tcustomizer-group-option');
      if (!w) return;
      var field = lbl.closest('.tcustomizer-field--Dropdown');
      if (field) field.setAttribute('data-db-picked', '1');
      setTimeout(function () { syncContinueBtn(w); }, 60);   // no auto-advance: Continue does that
    }, true);

    widget.addEventListener('change', function (e) {
      if (e.target.tagName !== 'SELECT') return;
      var w = e.target.closest('.tcustomizer-field.tcustomizer-group-option');
      if (!w) return;
      onSelectChange(w);
    });

    var visObs = new MutationObserver(debounce(function () {
      var w = wrappers[currentStep];
      if (w) {
        var a = accEl(w);
        var alreadyDone = a && a.querySelector('.db-acc-badge.db-badge-done');
        var hasSels = Array.from(w.querySelectorAll('select')).some(function (s) { return isVisible(s); });
        if (hasSels && getPrimary(w) && selectsOk(w) && !alreadyDone) {
          onSelectChange(w);
        }
        // Keep the Continue button in sync if "Other" toggled the text field.
        syncContinueBtn(w);
      }
      var fresh = getWrappers();
      if (fresh.length !== wrappers.length) {
        wrappers = fresh;
        if (currentStep >= wrappers.length) currentStep = wrappers.length - 1;
        buildLayout(wrappers);
        if (currentStep >= wrappers.length) currentStep = wrappers.length - 1;
        applyStates();
      }
    }, OBS_DEBOUNCE));
    visObs.observe(form, { subtree: true, attributes: true, attributeFilter: ['style', 'class'] });

    form.addEventListener('click', function (e) {
      // Don't treat the Continue button as a heading toggle.
      if (e.target.closest('.db-continue-btn')) return;

      var h = e.target.closest('.tcustomizer-accordion__heading');
      if (!h) return;
      var w = h.closest('.tcustomizer-field.tcustomizer-group-option');
      if (!w) return;
      var idx = wrappers.indexOf(w);
      if (idx === -1) return;


      if (idx > currentStep) {
        e.preventDefault();
        e.stopImmediatePropagation();
        var activeRailDot = sideStepEls[currentStep] && sideStepEls[currentStep].dot;
        if (activeRailDot) {
          activeRailDot.style.animation = 'none';
          requestAnimationFrame(function () { activeRailDot.style.animation = 'db-pulse .6s ease'; });
        }
        return false;
      }

      // Take over the toggle entirely so TEPO and our code don't both fire
      // (that double-toggle is what required two clicks). We open/close here.
      e.preventDefault();
      e.stopImmediatePropagation();

      var done = accEl(w) && accEl(w).querySelector('.db-acc-badge.db-badge-done');

      if (isOpen(w)) {
        closeAcc(w);
      } else {
        wrappers.forEach(function (ow, oi) { if (oi !== idx) closeAcc(ow); });
        openAcc(w);
        /* Re-opening a completed step means they want to change something, so
           the previous Continue no longer stands - make them confirm again. */
        if (hasDropdownGate(w)) w.removeAttribute('data-db-confirmed');
        if (!done) {
          currentStep = idx;
          setBadge(w, 'selecting');
        }
      }
      refreshHeadDescs();
      syncContinueBtn(w);
      updateSidebar(wrappers, currentStep);
      return false;
    }, true);
  }

  /* ═══════════════════════════════════════════════════
     INIT
  ═══════════════════════════════════════════════════ */
  function init() {
    injectStyles();
    wrappers = getWrappers();
    if (!wrappers.length) { setTimeout(init, 600); return; }

    console.log('[guided-options] v6.9 — ' + wrappers.length + ' steps');

    isolateRadioNames();

    currentStep = 0;
    for (var i = 0; i < wrappers.length; i++) {
      if (isComplete(wrappers[i])) {
        var v = getPrimary(wrappers[i]);
        if (v) wrappers[i].setAttribute('data-db-summary', v);
        var d = getDescText(wrappers[i]);
        if (d) wrappers[i].setAttribute('data-db-desc', d);
        currentStep = i + 1;
      } else { break; }
    }
    if (currentStep >= wrappers.length) currentStep = wrappers.length - 1;

    buildLayout(wrappers);
    applyStates();
    attachListeners();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(init, INIT_DELAY); });
  } else {
    setTimeout(init, INIT_DELAY);
  }

})();